import { useState, useEffect, useCallback } from 'react'
import { Bell, ArrowSquareOut, Warning, Info, Funnel, ArrowClockwise, Sparkle, CheckCircle, Circle, Database } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'

const LS_SUMMARIES_KEY = 'regulatory_ai_summaries'
const LS_SEEN_KEY = 'regulatory_seen_ids_v1'

function loadSummaries() {
  try {
    return JSON.parse(localStorage.getItem(LS_SUMMARIES_KEY) || '{}')
  } catch { return {} }
}

function saveSummaries(summaries) {
  try { localStorage.setItem(LS_SUMMARIES_KEY, JSON.stringify(summaries)) } catch {}
}

function loadSeenIds() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_SEEN_KEY) || '[]')) } catch { return new Set() }
}

function saveSeenIds(ids) {
  try { localStorage.setItem(LS_SEEN_KEY, JSON.stringify([...ids])) } catch {}
}

const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

// ── Datos estáticos de referencia (siempre visibles, pinned) ─────────────────
// Noticias importantes que deben mostrarse aunque falle EUR-Lex
const PINNED_ITEMS = [
  {
    id: 'pinned-1',
    title: 'Paquete Omnibus — CE propone retrasar CSRD para segunda ola y pausar ESRS sectoriales',
    date: '2025-02-26',
    category: 'CSRD',
    summary: 'La Comisión Europea publicó el paquete Omnibus de simplificación (COM(2025) 87). Propone retrasar 2 años la aplicación de CSRD para empresas de 250-1.000 empleados (segunda ola pasaría al ejercicio 2028) y reducir los datos requeridos. El desarrollo de los ESRS sectoriales —incluyendo el específico para inmobiliario— queda en pausa indefinida. Las empresas de la primera ola (grandes cotizadas >500 empleados) siguen obligadas desde el ejercicio 2024.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=COM:2025:87:FIN',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-2',
    title: 'EPBD Recast — Directiva 2024/1275/UE en vigor, transposición en España antes del 29 mayo 2026',
    date: '2024-05-08',
    category: 'EPBD',
    summary: 'La Directiva 2024/1275/UE (EPBD Recast) fue publicada en el DOUE el 8 de mayo de 2024. España debe transponerla antes del 29 de mayo de 2026. Obliga a renovar el 16% de los edificios no residenciales de peor rendimiento antes de 2030 y el 26% antes de 2033 (EPC E, F, G). Incluye requisitos de infraestructura para vehículos eléctricos y elimina progresivamente las calderas de combustibles fósiles en edificios nuevos.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202401275',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-3',
    title: 'ETS2 — Nuevo mercado de carbono para edificios y transporte: inicio enero 2027',
    date: '2023-05-16',
    category: 'Taxonomía UE',
    summary: 'La Directiva 2023/959/UE crea el ETS2, mercado de derechos de emisión para edificios y transporte. Entra en vigor en enero de 2027. Los distribuidores de combustibles deberán adquirir derechos de emisión, encareciendo el gasóleo y el gas para uso en edificios.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202300959',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-4',
    title: 'ESRS — Reglamento Delegado (UE) 2023/2772: estándares europeos de reporting de sostenibilidad',
    date: '2023-12-22',
    category: 'CSRD',
    summary: 'El Reglamento Delegado 2023/2772 aprueba los 12 ESRS genéricos (ESRS 1, ESRS 2, E1-E5, S1-S4, G1). Establece los requisitos de divulgación ESG para las empresas sujetas a CSRD. ESRS E1 (Cambio climático) y ESRS E4 (Biodiversidad) son especialmente relevantes para el sector inmobiliario y construcción.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202302772',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-5',
    title: 'Taxonomía UE — Reglamento Delegado 2021/2139: criterios técnicos de screening para construcción',
    date: '2021-12-09',
    category: 'Taxonomía UE',
    summary: 'Define qué actividades constructivas son "sostenibles" según la Taxonomía UE (contribución sustancial a mitigación/adaptación, DNSH). Para inmuebles: construcción de edificios nuevos (umbral 10% percentil NZEBs nacionales) y renovación con mejora ≥30% consumo energético. Clave para inversión sostenible, taxonomía SFDR y reporting CSRD.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202102139',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-6',
    title: 'SFDR — Reglamento (UE) 2019/2088: divulgación información sostenibilidad en servicios financieros',
    date: '2019-12-09',
    category: 'ESMA ESG',
    summary: 'El Reglamento SFDR clasifica productos financieros en Artículo 6, 8 (características ESG) y 9 (inversión sostenible). Los fondos inmobiliarios con exposición a activos verdes o certificados BREEAM/LEED suelen clasificarse como Art. 8+. Impacta valoración y due diligence de activos con certificaciones de sostenibilidad.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX:32019R2088',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-7',
    title: 'CSRD — Directiva 2022/2464/UE: reporting corporativo de sostenibilidad obligatorio desde 2024',
    date: '2022-12-14',
    category: 'CSRD',
    summary: 'La Corporate Sustainability Reporting Directive sustituye a NFRD y amplía el ámbito a ~50.000 empresas en la UE. Exige auditoría de la información de sostenibilidad. Para el sector inmobiliario implica reportar consumo energético, emisiones GEI, biodiversidad e impacto en comunidades de toda la cartera de activos.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202202464',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-8',
    title: 'Reglamento (UE) 2024/1167: revisión actos delegados Taxonomía — nuevas actividades construcción',
    date: '2024-04-30',
    category: 'Taxonomía UE',
    summary: 'Modifica los Reglamentos Delegados 2021/2139 y 2021/2178 de la Taxonomía UE. Introduce nuevas actividades económicas elegibles y ajusta criterios técnicos de screening para construcción y rehabilitación de edificios. Actualiza criterios DNSH de contaminación y economía circular aplicables a materiales de construcción.',
    impact: 'medio',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202401167',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-9',
    title: 'Directiva (UE) 2024/1760: Diligencia Debida en Sostenibilidad Empresarial (CS3D)',
    date: '2024-07-05',
    category: 'CSRD',
    summary: 'La Corporate Sustainability Due Diligence Directive obliga a grandes empresas a identificar y mitigar impactos negativos en derechos humanos y medio ambiente en su cadena de valor. Para constructoras y promotoras: afecta a cadena de suministro de materiales (áridos, acero, cemento), subcontratación de obra y gestión de residuos.',
    impact: 'alto',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202401760',
    source: 'static',
    pinned: true,
  },
  {
    id: 'pinned-10',
    title: 'Reglamento (UE) 2023/1115: deforestación — impacto en materiales de construcción de madera',
    date: '2023-06-09',
    category: 'Reglamento UE',
    summary: 'Prohíbe la comercialización en la UE de productos vinculados a deforestación. Afecta directamente a madera estructural, tableros OSB, CLT y otros productos madereros usados en construcción. Las empresas deben acreditar due diligence sobre el origen del material. Aplicación obligatoria desde diciembre 2024 para grandes operadores.',
    impact: 'medio',
    url: 'https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=OJ:L_202301115',
    source: 'static',
    pinned: true,
  },
]

const MACRO_INDICATORS = [
  { label: 'ETS2 para edificios', value: '2027', unit: 'año de inicio', trend: null, note: 'Dir. 2023/959/UE · Combustibles calefacción y transporte' },
  { label: 'CSRD primera ola', value: 'Ej. 2024', unit: '(informe 2025)', trend: null, note: 'Grandes cotizadas >500 emp. · Reg. Del. UE 2023/2772' },
  { label: 'Renovación no residencial EPBD', value: '16%', unit: 'peor parque antes 2030', trend: null, note: 'Edificios EPC E, F, G · Dir. 2024/1275/UE' },
  { label: 'Transposición EPBD en España', value: '29 may', unit: '2026 (plazo)', trend: 'up', note: 'CTE DB-HE y RD 390/2021 pendientes de revisión' },
]

const CATEGORIES = [
  'Todos', 'EPBD', 'Taxonomía UE', 'CSRD', 'ESMA ESG',
  'Normativa Técnica', 'Productos Construcción', 'Economía Circular',
  'Vivienda/Urbanismo', 'Energía Edificios', 'Medio Ambiente',
  'Normativa ES', 'Reglamento UE',
]
const IMPACTS = ['Todos', 'alto', 'medio', 'bajo']
const SOURCES = ['Todas', 'EUR-Lex', 'BOE', 'EFRAG', 'MITECO', 'MITMA']

const SOURCE_CONFIG = {
  eurlex:  { label: 'EUR-LEX',  color: '#8D9A2E', bg: 'rgba(141,154,46,0.1)',  border: 'rgba(141,154,46,0.25)' },
  boe:     { label: 'BOE',      color: '#D4614A', bg: 'rgba(212,97,74,0.1)',   border: 'rgba(212,97,74,0.25)' },
  efrag:   { label: 'EFRAG',    color: '#7A8FA0', bg: 'rgba(122,143,160,0.1)', border: 'rgba(122,143,160,0.25)' },
  miteco:  { label: 'MITECO',   color: '#C98A2E', bg: 'rgba(201,138,46,0.1)',  border: 'rgba(201,138,46,0.25)' },
  mitma:   { label: 'MITMA',    color: '#7A5FA0', bg: 'rgba(122,95,160,0.1)',  border: 'rgba(122,95,160,0.25)' },
  static:  { label: 'FIJO',     color: '#B0A89E', bg: 'rgba(176,168,158,0.1)', border: 'rgba(176,168,158,0.25)' },
}

const IMPACT_CONFIG = {
  alto:  { label: 'Alto',  color: ACCENT.coral },
  medio: { label: 'Medio', color: ACCENT.amber },
  bajo:  { label: 'Bajo',  color: '#B0A89E' },
}

const LS_KEY = 'regulatory_feed_cache_v4'
const CACHE_TTL_MS = 2 * 60 * 60 * 1000 // 2h — dentro del mismo día se refresca solo
const HISTORY_DAYS = 60 // ventana de histórico a mostrar

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw)
    if (Date.now() - cached.timestamp < CACHE_TTL_MS) return cached
  } catch {}
  return null
}

export default function MonitorRegulatorio() {
  const [catFilter, setCatFilter] = useState('Todos')
  const [impactFilter, setImpactFilter] = useState('Todos')
  const [sourceFilter, setSourceFilter] = useState('Todas')
  const [alertKeyword, setAlertKeyword] = useState('')

  const [liveItems, setLiveItems] = useState(() => loadFromStorage()?.items || [])
  const [loading, setLoading] = useState(false)
  const [fetchedAt, setFetchedAt] = useState(() => loadFromStorage()?.fetchedAt || null)
  const [fetchSource, setFetchSource] = useState(() => loadFromStorage()?.source || null)
  const [sourcesOk, setSourcesOk] = useState(() => loadFromStorage()?.sourcesOk || [])
  const [fetchError, setFetchError] = useState(null)

  // ── Detección de novedades ────────────────────────────────────────────────
  // Los ids vistos se persisten en localStorage vía saveSeenIds; no se
  // guardan en state porque la UI no necesita reaccionar a ellos.
  const [newItemIds, setNewItemIds] = useState(new Set())

  // ── Resúmenes IA ──────────────────────────────────────────────────────────
  const [summaries, setSummaries] = useState(() => loadSummaries())
  const [summarizingIds, setSummarizingIds] = useState(new Set())

  // ── Leer histórico desde regulatory_feed_items (compartido entre dispositivos)
  const loadHistoryFromDb = useCallback(async () => {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - HISTORY_DAYS)
    const cutoffIso = cutoff.toISOString().substring(0, 10)
    const { data, error } = await supabase
      .from('regulatory_feed_items')
      .select('id, title, date, category, impact, summary, url, source, first_seen_at, last_seen_at')
      .gte('date', cutoffIso)
      .order('date', { ascending: false })
      .limit(500)
    if (error) throw new Error(error.message)
    return (data || []).filter(item => !PINNED_ITEMS.some(p => p.url === item.url || p.id === item.id))
  }, [])

  // ── Obtener datos: primero DB (histórico), luego pedir refresh a Edge Function
  const fetchRegulatory = useCallback(async (forceRefresh = false) => {
    // Si no forzamos refresco y hay cache localStorage vigente (<2h), usarlo directamente
    if (!forceRefresh) {
      const cached = loadFromStorage()
      if (cached) {
        setLiveItems(cached.items)
        setFetchedAt(cached.fetchedAt)
        setFetchSource(cached.source)
        setSourcesOk(cached.sourcesOk || [])
        setFetchError(null)
        return
      }
    }

    setLoading(true)
    setFetchError(null)

    // Paso 1 — mostrar histórico DB inmediatamente (no depende de fuentes externas)
    let dbItems = []
    try {
      dbItems = await loadHistoryFromDb()
      if (dbItems.length > 0) {
        setLiveItems(dbItems)
        setFetchSource('db-history')
        setSourcesOk(['db'])
      }
    } catch (e) {
      console.warn('No se pudo leer histórico DB:', e.message)
    }

    // Paso 2 — pedir a la Edge Function que traiga novedades y persista en DB
    try {
      const { data, error } = await supabase.functions.invoke('regulatory-feed', {
        method: 'GET',
      })
      if (error) throw new Error(error.message)

      const fetchedAtVal = data?.fetchedAt || new Date().toISOString()
      const sourceVal = data?.source || 'unknown'
      const sourcesOkVal = data?.sourcesOk || []

      // Releer histórico DB (ahora incluye lo que acaba de guardar la función)
      let mergedItems = dbItems
      try {
        mergedItems = await loadHistoryFromDb()
      } catch {}

      const finalItems = mergedItems.length > 0 ? mergedItems : (data?.items || [])
        .filter(item => !PINNED_ITEMS.some(p => p.url === item.url || p.id === item.id))

      // Cache local para transiciones rápidas entre pestañas
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({
          items: finalItems, fetchedAt: fetchedAtVal, source: sourceVal, sourcesOk: sourcesOkVal, timestamp: Date.now()
        }))
      } catch {}

      // Detectar novedades respecto a la sesión anterior
      const prevIds = loadSeenIds()
      const freshNew = finalItems.filter(i => !prevIds.has(i.id))
      if (freshNew.length > 0) {
        setNewItemIds(new Set(freshNew.map(i => i.id)))
      }
      setLiveItems(finalItems)
      setFetchedAt(fetchedAtVal)
      setFetchSource(sourceVal)
      setSourcesOk(sourcesOkVal)

      const allSourcesFailed = (sourcesOkVal || []).length === 0
      if (data?.source === 'fallback' && allSourcesFailed && finalItems.length === 0) {
        setFetchError('Las fuentes externas no respondieron. Se muestran las referencias estáticas. Pulsa "Actualizar" más tarde.')
      }
    } catch (err) {
      console.error('Error fetching regulatory feed:', err)
      if (dbItems.length === 0) {
        setFetchError('No se pudo conectar con las fuentes. Pulsa "Actualizar" para reintentar.')
      }
      setFetchSource(prev => prev || (dbItems.length > 0 ? 'db-history' : 'offline'))
    } finally {
      setLoading(false)
    }
  }, [loadHistoryFromDb])

  // Marcar todos los ítems actuales como vistos tras 5 segundos
  useEffect(() => {
    if (liveItems.length === 0) return
    const timer = setTimeout(() => {
      const allCurrentIds = new Set([
        ...PINNED_ITEMS.map(p => p.id),
        ...liveItems.map(i => i.id),
      ])
      const merged = new Set([...loadSeenIds(), ...allCurrentIds])
      saveSeenIds(merged)
      setNewItemIds(new Set())
    }, 5000)
    return () => clearTimeout(timer)
  }, [liveItems])

  useEffect(() => {
    fetchRegulatory()
  }, [fetchRegulatory])

  // ── Auto-generar resúmenes IA para items nuevos ───────────────────────────
  const summarizeItem = useCallback(async (item) => {
    setSummarizingIds(prev => new Set([...prev, item.id]))
    try {
      const { data, error } = await supabase.functions.invoke('summarize-regulatory', {
        body: { item: { title: item.title, date: item.date, source: item.source, description: item.summary || '', url: item.url } },
      })
      if (error || !data?.ok) throw new Error(error?.message || data?.error)
      setSummaries(prev => {
        const updated = { ...prev, [item.id]: { resumen: data.resumen, puntos_clave: data.puntos_clave, impacto_sector: data.impacto_sector, plazo: data.plazo } }
        saveSummaries(updated)
        return updated
      })
    } catch (e) {
      console.warn('[Summarize] Error para', item.id, e.message)
    } finally {
      setSummarizingIds(prev => { const s = new Set(prev); s.delete(item.id); return s })
    }
  }, [])

  useEffect(() => {
    const allItems = [...PINNED_ITEMS, ...liveItems]
    const cached = loadSummaries()
    // Items sin resumen IA cacheado — máx 12 para no sobrecargar
    const toSummarize = allItems.filter(item => !cached[item.id]).slice(0, 12)
    if (toSummarize.length === 0) return
    // Escalonar las llamadas cada 300ms para no saturar
    toSummarize.forEach((item, i) => {
      setTimeout(() => summarizeItem(item), i * 350)
    })
  }, [liveItems, summarizeItem])

  // ── Combinar pinned + live, ordenar por fecha ─────────────────────────────
  const allItems = [...PINNED_ITEMS, ...liveItems].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  )

  const sourceFilterMap = { 'EUR-Lex': 'eurlex', 'BOE': 'boe', 'EFRAG': 'efrag', 'MITECO': 'miteco', 'MITMA': 'mitma' }
  const filtered = allItems.filter(item =>
    (catFilter === 'Todos' || item.category === catFilter) &&
    (impactFilter === 'Todos' || item.impact === impactFilter) &&
    (sourceFilter === 'Todas' || item.source === sourceFilterMap[sourceFilter] || (item.pinned && sourceFilter === 'Todas')) &&
    (!alertKeyword.trim() || alertKeyword.split(',').some(kw =>
      kw.trim() && item.title.toLowerCase().includes(kw.trim().toLowerCase())
    ))
  )

  const highCount = allItems.filter(r => r.impact === 'alto').length
  const medCount  = allItems.filter(r => r.impact === 'medio').length

  // ── Formato de timestamp ──────────────────────────────────────────────────
  const lastUpdatedText = fetchedAt
    ? new Date(fetchedAt).toLocaleString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>Monitor Regulatorio UE</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>Normativa — Edificación e Inmobiliario</div>
              {newItemIds.size > 0 && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#D4614A', color: '#FFFFFF', borderRadius: '4px', padding: '0.2rem 0.6rem', fontSize: '0.7rem', fontWeight: '700' }}>
                  <Bell size={11} weight="fill" />
                  {newItemIds.size} {newItemIds.size === 1 ? 'novedad nueva' : 'novedades nuevas'}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>
              EUR-Lex · BOE · EFRAG · MITECO · MITMA · Tiempo real
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Total novedades', value: allItems.length, color: '#F5F0EA' },
              { label: 'Impacto alto',    value: highCount,        color: ACCENT.coral },
              { label: 'Impacto medio',   value: medCount,         color: ACCENT.amber },
            ].map((kpi, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: kpi.color, lineHeight: 1 }}>{kpi.value}</div>
                <div style={{ fontSize: '0.65rem', color: '#B0A89E', marginTop: '0.2rem' }}>{kpi.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Botón refresh + estado */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
          <button
            onClick={() => fetchRegulatory(true)}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'rgba(245,240,234,0.1)', border: '1px solid rgba(245,240,234,0.2)',
              color: '#F5F0EA', borderRadius: '4px', padding: '0.45rem 0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.775rem', fontFamily: "'DM Sans', sans-serif", fontWeight: '500',
              opacity: loading ? 0.7 : 1,
            }}
          >
            <ArrowClockwise size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Consultando EUR-Lex…' : 'Actualizar ahora'}
          </button>
          {lastUpdatedText && (
            <div style={{ fontSize: '0.65rem', color: '#B0A89E', textAlign: 'right', display: 'flex', gap: '0.3rem', alignItems: 'center', justifyContent: 'flex-end' }}>
              {fetchSource === 'fallback'
                ? <Warning size={11} style={{ color: ACCENT.amber }} />
                : sourcesOk.length > 0
                  ? <CheckCircle size={11} style={{ color: ACCENT.olive }} />
                  : <Circle size={11} style={{ color: '#B0A89E' }} />}
              Actualizado: {lastUpdatedText}
            </div>
          )}
          {sourcesOk.length > 0 && (
            <div style={{ fontSize: '0.6rem', color: '#B0A89E', textAlign: 'right', display: 'flex', gap: '0.3rem', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Database size={10} />
              Histórico {HISTORY_DAYS} días · Fuentes: {sourcesOk.map(s => s.toUpperCase()).join(' · ')}
            </div>
          )}
        </div>
      </div>

      {/* ── Alerta solo si TODAS las fuentes fallan y no hay datos ── */}
      {fetchError && (
        <div style={{ background: '#FDF5F3', border: '1px solid #DDD5C8', borderLeft: '3px solid #C98A2E', borderRadius: '6px', padding: '0.875rem 1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <Warning size={16} style={{ color: '#C98A2E', flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#2C2017', marginBottom: '0.2rem' }}>
              {fetchSource === 'offline' ? 'Edge Function no disponible' : 'Fuentes externas sin respuesta'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#7A6A5A', lineHeight: '1.5' }}>
              {fetchError}
            </div>
          </div>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && liveItems.length === 0 && (
        <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#7A6A5A' }}>
          <ArrowClockwise size={16} style={{ animation: 'spin 1s linear infinite', color: '#8D9A2E' }} />
          <span style={{ fontSize: '0.875rem' }}>Consultando BOE y EFRAG…</span>
        </div>
      )}

      {/* ── Indicadores macro ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Indicadores Macro
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.875rem' }}>
          {MACRO_INDICATORS.map((ind, i) => (
            <div key={i} style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.875rem 1rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '500', color: '#7A6A5A', marginBottom: '0.35rem' }}>{ind.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                <span style={{ fontSize: '1.4rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017' }}>{ind.value}</span>
                <span style={{ fontSize: '0.7rem', color: '#7A6A5A' }}>{ind.unit}</span>
                {ind.trend === 'up' && <span style={{ color: ACCENT.coral, fontWeight: '600', fontSize: '0.8rem' }}>↑</span>}
              </div>
              <div style={{ fontSize: '0.67rem', color: '#B0A89E', marginTop: '0.2rem' }}>{ind.note}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Alertas configurables ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Filtro de palabras clave
        </h3>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Bell size={16} style={{ color: '#7A6A5A' }} />
          <span style={{ fontSize: '0.8rem', color: '#5C4A3A' }}>Filtrar por palabras clave:</span>
          <input
            type="text"
            placeholder="Ej: CSRD, taxonomía, EPBD…"
            value={alertKeyword}
            onChange={e => setAlertKeyword(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '0.45rem 0.75rem', border: '1px solid #DDD5C8', borderRadius: '4px', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif", color: '#2C2017', background: '#FAF7F3', outline: 'none' }}
          />
          {alertKeyword && (
            <button onClick={() => setAlertKeyword('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A89E', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif" }}>
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* ── Feed regulatorio ── */}
      <div style={CARD}>
        <div style={{ marginBottom: '1rem' }}>
          {/* Título */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
              Feed de Novedades Regulatorias
            </h3>
            <span style={{ fontSize: '0.7rem', color: '#B0A89E', paddingTop: '2px' }}>{filtered.length} resultados</span>
          </div>
          {/* Filtros en fila propia */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Funnel size={14} style={{ color: '#7A6A5A', flexShrink: 0 }} />
            <select
              value={catFilter}
              onChange={e => setCatFilter(e.target.value)}
              style={{ padding: '0.35rem 0.6rem', border: '1px solid #DDD5C8', borderRadius: '4px', fontSize: '0.775rem', fontFamily: "'DM Sans', sans-serif", color: '#2C2017', background: '#FAF7F3', outline: 'none', cursor: 'pointer' }}
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select
              value={impactFilter}
              onChange={e => setImpactFilter(e.target.value)}
              style={{ padding: '0.35rem 0.6rem', border: '1px solid #DDD5C8', borderRadius: '4px', fontSize: '0.775rem', fontFamily: "'DM Sans', sans-serif", color: '#2C2017', background: '#FAF7F3', outline: 'none', cursor: 'pointer' }}
            >
              {IMPACTS.map(i => <option key={i} value={i}>{i === 'Todos' ? 'Todos los niveles' : `Impacto ${i}`}</option>)}
            </select>
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              style={{ padding: '0.35rem 0.6rem', border: '1px solid #DDD5C8', borderRadius: '4px', fontSize: '0.775rem', fontFamily: "'DM Sans', sans-serif", color: '#2C2017', background: '#FAF7F3', outline: 'none', cursor: 'pointer' }}
            >
              {SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(item => {
            const impCfg = IMPACT_CONFIG[item.impact] || IMPACT_CONFIG.medio
            const isPinned = item.pinned
            const aiSummary = summaries[item.id]
            const isSummarizing = summarizingIds.has(item.id)
            const scfg = SOURCE_CONFIG[item.source] || SOURCE_CONFIG.static
            const isNew = newItemIds.has(item.id)
            const daysSince = (Date.now() - new Date(item.date).getTime()) / (1000 * 60 * 60 * 24)
            const isRecent = daysSince <= 30 && !item.pinned && !isNew

            return (
              <div key={item.id} style={{
                background: isNew ? 'rgba(212,97,74,0.04)' : '#FAF7F3',
                border: isNew ? '1px solid rgba(212,97,74,0.3)' : '1px solid #DDD5C8',
                borderLeft: `3px solid ${isNew ? '#D4614A' : impCfg.color}`,
                borderRadius: '6px',
                padding: '1rem 1.25rem',
                position: 'relative',
              }}>
                {/* Badges esquina superior derecha */}
                <div style={{ position: 'absolute', top: '0.625rem', right: '0.625rem', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                  {isNew && (
                    <div style={{ fontSize: '0.55rem', fontWeight: '700', textTransform: 'uppercase', color: '#FFFFFF', background: '#D4614A', borderRadius: '3px', padding: '0.15rem 0.45rem', letterSpacing: '0.06em' }}>
                      NUEVA
                    </div>
                  )}
                  {isRecent && (
                    <div style={{ fontSize: '0.55rem', fontWeight: '600', textTransform: 'uppercase', color: '#8D9A2E', background: 'rgba(141,154,46,0.12)', border: '1px solid rgba(141,154,46,0.3)', borderRadius: '3px', padding: '0.15rem 0.45rem', letterSpacing: '0.06em' }}>
                      RECIENTE
                    </div>
                  )}
                  {item.source && item.source !== 'static' && (
                    <div style={{ fontSize: '0.6rem', fontWeight: '600', color: scfg.color, background: scfg.bg, border: `1px solid ${scfg.border}`, borderRadius: '3px', padding: '0.1rem 0.4rem' }}>
                      {scfg.label} {item.source === 'eurlex' ? 'LIVE' : ''}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '0.4rem', paddingRight: '5.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                    <span style={{ background: 'transparent', border: `1px solid ${impCfg.color}`, color: impCfg.color, borderRadius: '3px', padding: '0.1rem 0.45rem', fontSize: '0.65rem', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {impCfg.label}
                    </span>
                    <span style={{ background: '#EDE7DE', color: '#5C4A3A', borderRadius: '3px', padding: '0.1rem 0.45rem', fontSize: '0.65rem', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {item.category}
                    </span>
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#2C2017', lineHeight: '1.4' }}>
                    {isPinned && <span style={{ marginRight: '0.4rem', fontSize: '0.75rem' }}>📌</span>}
                    {item.title}
                  </h4>
                </div>

                <div style={{ fontSize: '0.7rem', color: '#B0A89E', marginBottom: '0.625rem' }}>
                  {new Date(item.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>

                {/* ── Bloque de análisis IA ── */}
                {isSummarizing && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: '#8D9A2E', marginBottom: '0.625rem' }}>
                    <Sparkle size={13} style={{ animation: 'spin 1.5s linear infinite' }} />
                    <span>Generando análisis de impacto…</span>
                  </div>
                )}

                {aiSummary && !isSummarizing && (
                  <div style={{ background: 'rgba(141,154,46,0.06)', border: '1px solid rgba(141,154,46,0.2)', borderRadius: '5px', padding: '0.75rem 1rem', marginBottom: '0.625rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.5rem' }}>
                      <Sparkle size={12} color="#8D9A2E" weight="fill" />
                      <span style={{ fontSize: '0.65rem', fontWeight: '600', color: '#8D9A2E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Análisis IA</span>
                    </div>
                    {aiSummary.resumen && (
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#3B2F2F', lineHeight: '1.55' }}>
                        {aiSummary.resumen}
                      </p>
                    )}
                    {aiSummary.puntos_clave?.length > 0 && (
                      <ul style={{ margin: '0 0 0.4rem', paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {aiSummary.puntos_clave.map((punto, i) => (
                          <li key={i} style={{ fontSize: '0.775rem', color: '#4A3A2A', lineHeight: '1.5' }}>{punto}</li>
                        ))}
                      </ul>
                    )}
                    {(aiSummary.impacto_sector || aiSummary.plazo) && (
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        {aiSummary.impacto_sector && (
                          <div style={{ fontSize: '0.72rem', color: '#5C4A3A', background: 'rgba(201,138,46,0.1)', border: '1px solid rgba(201,138,46,0.2)', borderRadius: '4px', padding: '0.2rem 0.6rem' }}>
                            🏗️ {aiSummary.impacto_sector}
                          </div>
                        )}
                        {aiSummary.plazo && (
                          <div style={{ fontSize: '0.72rem', color: '#5C4A3A', background: 'rgba(212,97,74,0.08)', border: '1px solid rgba(212,97,74,0.2)', borderRadius: '4px', padding: '0.2rem 0.6rem' }}>
                            📅 {aiSummary.plazo}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!aiSummary && !isSummarizing && (
                  <p style={{ margin: '0 0 0.625rem', fontSize: '0.8rem', color: '#5C4A3A', lineHeight: '1.6' }}>
                    {item.summary}
                  </p>
                )}

                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: '#7A6A5A', textDecoration: 'none', fontWeight: '500' }}
                >
                  <ArrowSquareOut size={13} /> Ver texto completo
                </a>
              </div>
            )
          })}

          {filtered.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#B0A89E' }}>
              <Info size={32} style={{ display: 'block', margin: '0 auto 0.5rem', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>No hay novedades con los filtros seleccionados</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Fuentes ── */}
      <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem 1.25rem' }}>
        <p style={{ fontSize: '0.75rem', color: '#7A6A5A', margin: 0, lineHeight: '1.6' }}>
          <strong style={{ color: '#5C4A3A' }}>Fuentes:</strong> EUR-Lex CELLAR SPARQL API (Publications Office of the EU) · BOE · EFRAG · MITECO · MITMA.
          Cubre normativa de edificación, productos de construcción, eficiencia energética, urbanismo, economía circular, ESG y financiación verde.{' '}
          <strong style={{ color: '#D4614A' }}>Verifica siempre el estado vigente en las fuentes oficiales antes de tomar decisiones.</strong> Esta aplicación no sustituye el asesoramiento jurídico especializado.
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
